import { useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Container,
  Typography,
  Alert,
  Snackbar,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Button,
} from "@mui/material";
import { useLetterhead } from "./hooks/useLetterhead.ts";

// Components
import { templateSchema, extractErrors } from "./services/templateValidationSchema.ts";
import CompanyInfoCard from "./components/template/CompanyInfoCard.js";
import BasicInfoCard from "./components/template/BasicInfoCard.js";
import DesignCard from "./components/template/DesignCard.js";
import PreviewCard from "./components/template/TemplatePreview.js";

export default function CreateTemplatePage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { templateState, setTemplateState, isLoading, handleSave, isEditMode } =
    useLetterhead();
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  const validateAndSave = async () => {
    try {
      setErrors({});
      await templateSchema.validate(templateState, { abortEarly: false });
      await handleSave();
      setSnackbar({
        open: true,
        message: "Template saved successfully!",
        severity: "success",
      });
    } catch (err) {
      if (err.name === "ValidationError") {
        const validationErrors = extractErrors(err);
        setErrors(validationErrors);
      } else if (
        JSON.stringify(err).toLowerCase().includes("exist") ||
        JSON.stringify(err).toLowerCase().includes("duplicate")
      ) {
        setErrors({
          templateName: "Template name already exists for this company.",
        });
      } else {
        console.error("Save failed:", err);
        setSnackbar({
          open: true,
          message: "Failed to save template. Please try again.",
          severity: "error",
        });
      }

      document
        .getElementById("form-scroll-container")
        ?.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        bgcolor: "grey.50",
        overflow: "hidden",
      }}
    >
      {/* Header */}

        <Container maxWidth="xl">
          <Typography variant="h5" fontWeight={600}>
            {isEditMode ? "Edit Letterhead Template" : "Create Letterhead Template"}
          </Typography>
        </Container>

      {/* Main Content */}
      <Box sx={{ flex: 1, overflow: "hidden", py: 3 }}>
        <Container maxWidth="xl" sx={{ height: "100%" }}>
          <Grid container spacing={3} sx={{ height: "100%" }}>
            {/* Left Column - Form */}
            <Grid
              item
              xs={12}
              md={7}
              sx={{
                height: "100%",
                overflow: "hidden",
                position: "relative",
              }}
            >
              <Box
                id="form-scroll-container"
                sx={{
                  height: "100%",
                  overflowY: "auto",
                  pr: 2,
                  pb: 4,
                  "&::-webkit-scrollbar": {
                    width: "6px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "transparent",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "grey.300",
                    borderRadius: "4px",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    background: "grey.400",
                  },
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <BasicInfoCard
                    templateState={templateState}
                    setTemplateState={setTemplateState}
                    errors={errors}
                  />
                  <CompanyInfoCard
                    templateState={templateState}
                    setTemplateState={setTemplateState}
                    errors={errors}
                  />
                  <DesignCard
                    templateState={templateState}
                    setTemplateState={setTemplateState}
                    errors={errors}
                  />

                  {/* Action Buttons */}
                  <Paper elevation={2} sx={{ p: 3, mt: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        justifyContent: "flex-end",
                        flexWrap: "wrap",
                      }}
                    >
                      {/* <Button
                        variant="outlined"
                        onClick={() => {
                          // Add reset/cancel logic
                        }}
                        disabled={isLoading}
                      >
                        Cancel
                      </Button> */}
                      <Button
                        variant="contained"
                        onClick={validateAndSave}
                        disabled={isLoading}
                        startIcon={
                          isLoading ? <CircularProgress size={20} /> : null
                        }
                        sx={{ minWidth: 120 }}
                      >
                        {isLoading ? "Saving..." : "Save Template"}
                      </Button>
                    </Box>
                  </Paper>
                </Box>
              </Box>
            </Grid>

            {/* Right Column - Preview */}
            <Grid
              item
              xs={12}
              md={5}
              sx={{
                height: "100%",
                display: { xs: "none", md: "block" },
                overflow: "hidden"
              }}
            >
              <Paper
                elevation={2}
                sx={{
                  height: "100%",
                  // height:"auto",
                  overflow: "auto",
                  p: 2,
                  // marginBottom:"20px",
                  bgcolor: "white",
                  "&::-webkit-scrollbar": {
                    width: "6px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "transparent",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "grey.300",
                    borderRadius: "4px",
                  },
                }}
              >
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                  Live Preview 
                </Typography>
                <PreviewCard state={templateState} />
              </Paper>
            </Grid>

            {/* Mobile Preview Toggle (Optional) */}
            {isMobile && (
              <Grid item xs={12}>
                <Paper elevation={2} sx={{ p: 2 }}>
                  <PreviewCard state={templateState} />
                </Paper>
              </Grid>
            )}
          </Grid>
        </Container>
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          elevation={6}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}